import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SortableTH, useSortableRows } from "@/components/SortableTH";
import { Notice } from "@/components/Notice";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiError } from "@/lib/apiClient";
import { applications, type ApplicationListItem } from "@/features/applications/api/applications.api";
import {
  StatusPill,
  APPLICATION_STATUS_TONES,
  APPLICATION_STATUS_LABELS,
} from "@/components/StatusPill";

type SortKey = "dbaName" | "submittedAt" | "status" | "ownerName";

export default function ApplicationsPage() {
  const [rows, setRows] = useState<ApplicationListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    let cancelled = false;
    applications.list()
      .then((data) => { if (!cancelled) setRows(data); })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 403) setForbidden(true);
        else setError(e instanceof Error ? e.message : "Failed to load applications.");
      });
    return () => { cancelled = true; };
  }, []);

  const sortValueGetters = useMemo(
    () => ({
      ownerName: (a: ApplicationListItem) => a.owner?.name ?? "",
    }),
    [],
  );
  const { sortedRows, sortKey, direction, setSort } = useSortableRows<ApplicationListItem, SortKey>(
    rows ?? [],
    "submittedAt",
    "desc",
    sortValueGetters,
  );

  return (
    <>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-800">Applications</h1>
        <p className="text-sm text-slate-500 mt-1">
          Boarding applications you can see. Visibility follows the standard
          rule: owner, associate, or manager-of-owner. Admins see every
          application — including anonymous submissions from the public form.
        </p>
      </header>

      {forbidden && (
        <Notice tone="warn">
          You don't have permission to view applications. Ask an admin to
          assign a role granting <code>30.03</code> (View only relevant
          Boarding Applications).
        </Notice>
      )}
      {error && !forbidden && <Notice tone="error">{error}</Notice>}
      {!forbidden && !error && rows === null && (
        <p className="text-muted-foreground text-sm">Loading applications…</p>
      )}

      {!forbidden && !error && rows !== null && rows.length === 0 && (
        <Notice tone="info">
          No applications visible yet. Applications arrive from the public
          onboarding form (a future slice) or are created in-portal by an
          user.
        </Notice>
      )}

      {!forbidden && !error && rows !== null && rows.length > 0 && (
        <div className="rounded-md border bg-white shadow-sm">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTH sortKey="dbaName" current={sortKey} direction={direction} onClick={setSort}>DBA</SortableTH>
                <SortableTH sortKey="submittedAt" current={sortKey} direction={direction} onClick={setSort}>Submitted</SortableTH>
                <SortableTH sortKey="status" current={sortKey} direction={direction} onClick={setSort}>Status</SortableTH>
                <SortableTH sortKey="ownerName" current={sortKey} direction={direction} onClick={setSort}>Owner</SortableTH>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRows.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="font-medium text-slate-900">
                    <Link to={`/applications/${a.id}`} className="hover:underline text-slate-900">
                      {a.dbaName}
                    </Link>
                    {a.legalName && a.legalName !== a.dbaName && (
                      <div className="text-xs text-slate-500">{a.legalName}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-slate-700 tabular-nums">
                    {new Date(a.submittedAt).toLocaleDateString(undefined, {
                      year: "numeric", month: "short", day: "numeric",
                    })}
                  </TableCell>
                  <TableCell>
                    <StatusPill
                      value={a.status}
                      map={APPLICATION_STATUS_TONES}
                      labels={APPLICATION_STATUS_LABELS}
                    />
                  </TableCell>
                  <TableCell className="text-slate-700">
                    {a.owner ? (
                      <>
                        {a.owner.name}
                        {a.owner.userNumber != null && (
                          <span className="text-slate-400"> #{a.owner.userNumber}</span>
                        )}
                        {a.associate && (
                          <div className="text-xs text-slate-500">Associate: {a.associate.name}</div>
                        )}
                      </>
                    ) : (
                      <span className="text-slate-400 italic">Unassigned</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="px-3 py-2 text-xs text-slate-500 border-t border-slate-200">
            {rows.length} application{rows.length === 1 ? "" : "s"}
          </div>
        </div>
      )}
    </>
  );
}
